import { useCallback } from "react";
import type { Stripe, StripeElements } from "@stripe/stripe-js";
import { CardNumberElement } from "@stripe/react-stripe-js";
import type { PaymentResult } from "@/interfaces";
import { buildRedirectUrl } from "@/lib/redirect-utils";
import { updateCardSave } from "@/services/PaymentService";

export const useStripePayment = () => {
  const processCardPayment = useCallback(
    async (
      stripe: Stripe,
      elements: StripeElements,
      clientSecret: string,
      email: string,
      saveCard: boolean = false,
      sessionId?: string
    ): Promise<PaymentResult> => {
      try {
        const cardNumberElement = elements.getElement(CardNumberElement);
        if (!cardNumberElement) {
          return {
            success: false,
            error: "Card number element not found",
          };
        }

        // Update card save preference on the server before confirming payment
        if (sessionId) {
          try {
            await updateCardSave({
              saveCard,
              sessionId,
            });
          } catch (error) {
            console.error("Failed to update card save preference:", error);
            // Continue with payment even if this fails, as it's not critical
          }
        }

        const confirmationData: any = {
          payment_method: {
            card: cardNumberElement,
            billing_details: {
              email: email,
            },
          },
        };

        // Only add setup_future_usage if we want to save the card
        if (saveCard) {
          confirmationData.setup_future_usage = 'off_session';
        }

        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          confirmationData
        );

        if (error) {
          console.error("Payment error:", error);
          
          // Use Stripe's error message directly - they're already user-friendly
          const errorMessage = error.message || "Payment failed. Please try again or use a different payment method.";
          
          return {
            success: false,
            error: errorMessage,
            redirectUrl: buildRedirectUrl('error', errorMessage),
          };
        }

        if (paymentIntent) {
          switch (paymentIntent.status) {
            case "succeeded":
              return {
                success: true,
                redirectUrl: buildRedirectUrl('success'),
                paymentMethodId: paymentIntent.payment_method as string,
              };

            case "requires_action":
              if (paymentIntent.next_action?.type === "use_stripe_sdk") {
                const { error: confirmError } = await stripe.confirmCardPayment(
                  clientSecret
                );
                if (confirmError) {
                  return {
                    success: false,
                    error: confirmError.message || "Authentication failed",
                  };
                } else {
                  return {
                    success: true,
                    redirectUrl: buildRedirectUrl('success'),
                    paymentMethodId: paymentIntent.payment_method as string,
                  };
                }
              } else {
                return {
                  success: false,
                  error:
                    "Additional authentication required. Please try again.",
                };
              }

            case "requires_payment_method":
              return {
                success: false,
                error: "Payment failed. Please try another payment method.",
              };

            case "processing":
              return {
                success: true,
                redirectUrl: buildRedirectUrl('success'),
                paymentMethodId: paymentIntent.payment_method as string,
              };

            default:
              return {
                success: false,
                error: `Unexpected payment status: ${paymentIntent.status}`,
              };
          }
        } else {
          return {
            success: false,
            error: "Payment failed. No payment intent received.",
          };
        }
      } catch (err) {
        console.error("Payment confirmation error:", err);
        return {
          success: false,
          error: "An unexpected error occurred",
        };
      }
    },
    []
  );

  return {
    processCardPayment,
  };
};
